import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { toast } from "sonner";
import EncyclopediaComments from "@/components/EncyclopediaComments";
import { resolveAssetUrl } from "@/components/ImageUploader";
import Seo from "@/components/Seo";
import {
  Library, Sparkles, Sprout, Leaf, Droplets, ClipboardCheck, Award,
  PieChart, Gauge, Droplet, AlertTriangle, Flame, Cog, Coffee,
  Zap, Milk, Brush, Wrench, Eye, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, ArrowUp, CheckCircle2
} from "lucide-react";

const ICON_MAP = {
  Sparkles, Sprout, Leaf, Droplets, ClipboardCheck, Award,
  PieChart, Gauge, Droplet, AlertTriangle, Flame, Cog, Coffee,
  Zap, Milk, Brush, Wrench, Eye, Library
};

// Section block — renders 'body' text and/or 'items' grid
const SectionBlock = ({ section, lang, isRTL, serifClass }) => {
  const heading = lang === "ar" ? section.heading_ar : section.heading_en;
  const body = lang === "ar" ? section.body_ar : section.body_en;
  return (
    <div className="mb-16" data-testid="enc-section">
      <h3 className={`${serifClass} text-2xl lg:text-3xl text-[#2A2421] mb-6 leading-tight`}>
        {heading}
      </h3>
      {body && (
        <p className="text-[#5C544F] leading-[1.85] max-w-3xl text-base lg:text-[17px]">
          {body}
        </p>
      )}
      {section.items && section.items.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-7">
          {section.items.map((item) => (
            <ItemCard
              key={(lang === "ar" ? item.name_ar : item.name_en) || item.name_en}
              item={item}
              lang={lang}
              isRTL={isRTL}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ItemCard = ({ item, lang, isRTL }) => {
  const name = lang === "ar" ? item.name_ar : item.name_en;
  const desc = lang === "ar" ? item.desc_ar : item.desc_en;
  return (
    <div
      className="group p-5 rounded-2xl border border-[#2A2421]/10 bg-[#F9F6F0] hover:bg-white hover:border-[#C86240]/30 transition-all duration-300"
      data-testid="enc-item"
    >
      <div className={`text-sm font-semibold text-[#2A2421] mb-2 ${isRTL ? "font-serif-ar text-base" : ""}`}>
        {name}
      </div>
      <p className="text-sm text-[#5C544F] leading-relaxed">{desc}</p>
    </div>
  );
};

const ModulePager = ({ modules, idx, setIdx, lang, isRTL }) => {
  const prev = modules[idx - 1];
  const next = modules[idx + 1];
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;
  return (
    <div className="mt-20 pt-12 border-t border-[#2A2421]/10 grid sm:grid-cols-2 gap-4" data-testid="enc-pager">
      {prev ? (
        <button
          onClick={() => setIdx(idx - 1)}
          className="text-left p-5 rounded-2xl border border-[#2A2421]/10 hover:bg-[#2A2421]/5 transition-all"
          data-testid="enc-prev-module"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F] flex items-center gap-1">
            <PrevIcon className="w-3 h-3" /> {isRTL ? "السابق" : "Previous"}
          </div>
          <div className="text-[#2A2421] font-medium mt-2">
            {lang === "ar" ? prev.title_ar : prev.title_en}
          </div>
        </button>
      ) : <div />}
      {next && (
        <button
          onClick={() => setIdx(idx + 1)}
          className="text-right p-5 rounded-2xl border border-[#2A2421]/10 hover:bg-[#2A2421]/5 transition-all sm:text-right"
          data-testid="enc-next-module"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-[#5C544F] flex items-center gap-1 justify-end">
            {isRTL ? "التالي" : "Next"} <NextIcon className="w-3 h-3" />
          </div>
          <div className="text-[#2A2421] font-medium mt-2">
            {lang === "ar" ? next.title_ar : next.title_en}
          </div>
        </button>
      )}
    </div>
  );
};

export default function Encyclopedia() {
  const { t, lang, isRTL } = useI18n();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const { data, isLoading } = useQuery({
    queryKey: ["encyclopedia"],
    queryFn: () => api.get("/encyclopedia").then((r) => r.data),
  });
  const [idx, setIdx] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  const contentRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const modules = useMemo(() => data?.modules || [], [data]);
  const active = modules[idx];

  // Deep link: ?m=<module_id> sets the active module and scrolls to it.
  useEffect(() => {
    const mId = searchParams.get("m");
    if (!mId || !modules.length) return;
    const i = modules.findIndex((m) => m.id === mId);
    if (i >= 0 && i !== idx) {
      setIdx(i);
      setPickerOpen(false);
      setTimeout(() => {
        contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
    // Clean up the param once consumed so back-nav stays sane.
    const next = new URLSearchParams(searchParams);
    next.delete("m");
    const currentHash = (typeof window !== "undefined" && window.location.hash) || "";
    setSearchParams(next, { replace: true });
    if (currentHash && typeof window !== "undefined" && !window.location.hash) {
      window.history.replaceState(null, "", `${window.location.pathname}${next.toString() ? `?${next.toString()}` : ""}${currentHash}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules]);

  // Fetch user's completed modules (only if logged in)
  const { data: progressData } = useQuery({
    queryKey: ["progress-encyclopedia", user?.user_id],
    queryFn: () => endpoints.listProgress("encyclopedia"),
    enabled: !!user,
  });
  const completedSet = useMemo(
    () => new Set(progressData?.completed || []),
    [progressData],
  );

  // Auto-mark the currently viewed module as completed after a short dwell.
  // We wait 8 seconds with the same module active before marking — gives the
  // user enough time to actually read at least a section.
  useEffect(() => {
    if (!user || !active) return;
    if (completedSet.has(active.id)) return;
    const timer = setTimeout(async () => {
      try {
        const res = await endpoints.markProgressComplete("encyclopedia", active.id);
        queryClient.invalidateQueries({ queryKey: ["progress-encyclopedia", user.user_id] });
        queryClient.invalidateQueries({ queryKey: ["progress-summary", user.user_id] });
        if (Array.isArray(res?.newly_unlocked)) {
          res.newly_unlocked.forEach((a) => {
            const title = isRTL ? a.title_ar : a.title_en;
            toast.success(isRTL ? `🏆 إنجاز جديد: ${title}` : `🏆 New achievement: ${title}`);
          });
        }
      } catch (_) {
        /* silent */
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [active, user, completedSet, queryClient, isRTL]);

  if (isLoading || !data) {
    return (
      <div className="max-w-4xl mx-auto p-20 text-[#5C544F]" data-testid="encyclopedia-loading">
        {t.common.loading}
      </div>
    );
  }

  const ActiveIcon = active ? (ICON_MAP[active.icon] || Library) : Library;

  const selectModule = (i) => {
    setIdx(i);
    setPickerOpen(false);
    // Scroll smoothly to the content area after the state update flushes.
    setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const scrollToPicker = () => {
    pickerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setPickerOpen(true);
  };

  return (
    <div className="bg-[#F9F6F0]" data-testid="encyclopedia-page">
      <Seo
        title={isRTL ? "موسوعة القهوة المختصة — من البذرة إلى الفنجان" : "Specialty Coffee Encyclopedia — Seed to Cup"}
        description={isRTL
          ? "موسوعة شاملة لعلوم القهوة المختصة بالعربية: أصول البن وأنواعه، التحميص، الاستخلاص، تحليل القهوة الحسي، عجلة النكهات SCA، طرق التخمير V60، الإيروبرس، الإسبريسو والكيمكس."
          : "Comprehensive specialty coffee encyclopedia: origins, varieties, roasting, extraction science, sensory analysis, SCA flavor wheel, and every brewing method explained."}
        keywords="موسوعة القهوة, علوم القهوة, أصول القهوة, أنواع البن, تحميص القهوة المختصة, استخلاص القهوة, تحليل القهوة الحسي, عجلة النكهات, تخمير V60, تخمير الإيروبرس, تخمير الكيمكس, تخمير الإسبريسو, شرح التحميص للقهوة المختصة"
        path="/encyclopedia"
      />
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-[#2A2421]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 lg:pt-12 pb-12 lg:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-[#C86240] flex items-center justify-center">
                  <Library className="w-5 h-5 text-white" />
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#C86240]">
                  {isRTL ? "موسوعة" : "Encyclopedia"}
                </div>
              </div>
              <h1 className={`${serifClass} text-4xl sm:text-5xl lg:text-7xl text-[#2A2421] leading-[0.95]`}>
                {lang === "ar" ? data.title_ar : data.title_en}
              </h1>
              <p className="mt-6 text-lg text-[#5C544F] leading-relaxed max-w-2xl">
                {lang === "ar" ? data.subtitle_ar : data.subtitle_en}
              </p>
            </div>
            <div className="lg:col-span-4 bg-[#2A2421] text-white rounded-2xl p-7 grid grid-cols-3 gap-4">
              <Stat label={isRTL ? "موديول" : "Modules"} value={modules.length} serifClass={serifClass} />
              <Stat label="SCA" value="✓" serifClass={serifClass} />
              <Stat label={isRTL ? "لغتان" : "Bilingual"} value="2" serifClass={serifClass} />
            </div>
          </div>
        </div>
      </div>

      {/* MODULE PICKER (collapsible) */}
      <div ref={pickerRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 lg:pt-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-3">
          {isRTL ? "اختر موديول" : "Pick a module"}
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-[#2A2421] text-white hover:bg-[#3A3128] transition-colors shadow-sm"
          data-testid="enc-picker-toggle"
          aria-expanded={pickerOpen}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 shrink-0 rounded-lg bg-[#C86240] flex items-center justify-center">
              <ActiveIcon className="w-5 h-5 text-white" />
            </span>
            <div className="text-left min-w-0">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">
                {isRTL ? "الموديول الحالي" : "Current module"}
              </div>
              <div className={`${serifClass} text-base lg:text-lg leading-tight truncate`}>
                {lang === "ar" ? active.title_ar : active.title_en}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 shrink-0">
            <span className="hidden sm:inline">
              {pickerOpen
                ? (isRTL ? "إخفاء القائمة" : "Hide list")
                : (isRTL ? "اعرض كل الموديولات" : "Show all modules")}
            </span>
            {pickerOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </button>

        {pickerOpen && (
          <div
            className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 p-4 rounded-2xl bg-white border border-[#2A2421]/10"
            data-testid="enc-picker-list"
          >
            {modules.map((m, i) => {
              const Icon = ICON_MAP[m.icon] || Library;
              const isActive = i === idx;
              const isDone = completedSet.has(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => selectModule(i)}
                  data-testid={`enc-nav-${m.id}`}
                  className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl transition-all ${
                    isActive
                      ? "bg-[#2A2421] text-[#F9F6F0]"
                      : "text-[#2A2421] hover:bg-[#2A2421]/5 border border-transparent hover:border-[#C86240]/20"
                  }`}
                >
                  <span className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${
                    isActive ? "bg-[#C86240]" : "bg-[#2A2421]/5"
                  }`}>
                    <Icon className={`w-4 h-4 ${isActive ? "text-white" : "text-[#2A2421]"}`} />
                  </span>
                  <span className="text-sm flex-1 leading-tight">
                    {lang === "ar" ? m.title_ar : m.title_en}
                  </span>
                  {isDone && (
                    <CheckCircle2
                      className={`w-4 h-4 shrink-0 ${isActive ? "text-[#7FB287]" : "text-[#4A9966]"}`}
                      data-testid={`enc-done-${m.id}`}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div ref={contentRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16">
        <main className="w-full" data-testid="enc-content">
          {/* Back to picker pill + progress */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <button
              type="button"
              onClick={scrollToPicker}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-[#2A2421]/10 text-sm text-[#2A2421] hover:bg-[#C86240] hover:text-white hover:border-[#C86240] transition-all shadow-sm"
              data-testid="enc-back-to-picker"
            >
              <ArrowUp className="w-4 h-4" />
              <span>{isRTL ? "اختر موديول آخر" : "Pick another module"}</span>
            </button>
            <div className="flex items-center gap-3 min-w-[180px] sm:min-w-[280px]" data-testid="enc-progress">
              <span className="text-xs uppercase tracking-[0.25em] text-[#5C544F] whitespace-nowrap">
                {isRTL ? `${idx + 1} من ${modules.length}` : `${idx + 1} of ${modules.length}`}
              </span>
              <div className="flex-1 h-1.5 rounded-full bg-[#2A2421]/10 overflow-hidden">
                <div
                  className="h-full bg-[#C86240] transition-all"
                  style={{ width: `${((idx + 1) / modules.length) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Module hero */}
          <div className="mb-12">
            <div
              className="relative h-56 sm:h-72 lg:h-80 rounded-3xl overflow-hidden mb-8"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(42,36,33,0.1) 0%, rgba(42,36,33,0.85) 100%), url(${resolveAssetUrl(active.hero_image)})`,
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
              data-testid="enc-module-hero"
            >
              <div className="absolute inset-0 flex flex-col justify-end p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-9 h-9 rounded-full bg-[#C86240] flex items-center justify-center">
                    <ActiveIcon className="w-4 h-4 text-white" />
                  </span>
                  <span className="text-xs uppercase tracking-[0.3em] text-white/80">
                    {String(idx + 1).padStart(2, "0")} · {modules.length}
                  </span>
                </div>
                <h2 className={`${serifClass} text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.02]`}>
                  {lang === "ar" ? active.title_ar : active.title_en}
                </h2>
                <p className="text-white/85 mt-3 text-base lg:text-lg max-w-2xl">
                  {lang === "ar" ? active.tagline_ar : active.tagline_en}
                </p>
              </div>
            </div>

            {/* Sections */}
            <div>
              {active.sections.map((section, sidx) => (
                <SectionBlock
                  key={`${active.id}-${sidx}-${section.heading_en || ""}`}
                  section={section}
                  lang={lang}
                  isRTL={isRTL}
                  serifClass={serifClass}
                />
              ))}
            </div>

            {/* User takeaways / comments */}
            <EncyclopediaComments moduleId={active.id} />

            {/* Pager */}
            <ModulePager
              modules={modules}
              idx={idx}
              setIdx={selectModule}
              lang={lang}
              isRTL={isRTL}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

const Stat = ({ label, value, serifClass }) => (
  <div>
    <div className={`${serifClass} text-3xl text-white`}>{value}</div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mt-1">{label}</div>
  </div>
);
